// New save pipeline: emit ONE correction per sub-turn, covering the
// entire paragraph.  No character-level diff, no LCS, no snap-out.
//
// Replaces the multi-step computeCorrections pipeline (diff -> snap-out
// -> anchor -> coalesce -> emit).  The old pipeline produced data-corruption
// on multi-token numbers: editing "100" (tokenized as ` 1`, `0`, `0`)
// to "50000" emitted a single correction `original_text="1" ->
// corrected_text="5000"` because the LCS greedily matched the leading `1`
// and trailing `0` characters, leaving adjacent "0" tokens intact.
// The rendered result was "500000" instead of "50000".
//
// The new rule (2026-05-29 DECISIONS.md):
//   On Save, emit exactly ONE corrections row covering the whole sub-turn,
//   with corrected_text = canonicalize(editorBufferText)
//   and    original_text = canonicalize(assembleVerbatim(subTurn.tokens))
//   and    start_ms      = subTurn.tokens[0].start_ms
//   and    end_ms        = subTurn.tokens[last].end_ms
//
// Empty-edit short-circuit (still load-bearing):
//   if canonicalize(editorBufferText) === canonicalize(assembleResolvedBefore(...))
//   -> no DB write, no edit_session wrapper.
//
// The RPC's half-open overlap predicate (start_ms < v_end_ms AND end_ms >
// v_start_ms) means the single whole-sub-turn correction dismisses any prior
// corrections inside the range — including narrower Claude (sentinel) corrections.

import type { SonioxToken, AcceptedCorrection, CandidateCorrection } from "./types";
import { canonicalize } from "./canonicalize";
import { assembleResolvedBefore, assembleVerbatim } from "./resolutionTokens";

/**
 * Build zero or one CandidateCorrection for a WYSIWYG save operation.
 *
 * Returns an empty array (no DB write) when the canonicalized edited text
 * equals the canonicalized resolved-before string — i.e. no visible change
 * vs. what the user saw when they entered edit mode.
 *
 * Returns a one-element array otherwise.  The single candidate covers the
 * full sub-turn token range (tokens[0].start_ms .. tokens[last].end_ms).
 *
 * @param sonioxTokens   All Soniox tokens for this sub-turn.
 * @param acceptedCorrections  Currently-accepted corrections overlapping
 *                             the sub-turn (used to build resolvedBefore for
 *                             the empty-edit comparison only).
 * @param editedText     Raw textContent from the contenteditable element.
 */
export function buildWholeSubTurnCorrection(
  sonioxTokens: SonioxToken[],
  acceptedCorrections: AcceptedCorrection[],
  editedText: string,
): CandidateCorrection[] {
  if (sonioxTokens.length === 0) return [];

  const resolvedBefore = canonicalize(assembleResolvedBefore(sonioxTokens, acceptedCorrections));
  const edited = canonicalize(editedText);

  // Empty-edit short-circuit: what the user sees hasn't changed.
  if (edited === resolvedBefore) return [];

  const firstToken = sonioxTokens[0];
  const lastToken = sonioxTokens[sonioxTokens.length - 1];

  const original_text = assembleVerbatim(sonioxTokens, firstToken.start_ms, lastToken.end_ms);

  return [
    {
      start_ms: firstToken.start_ms,
      end_ms: lastToken.end_ms,
      corrected_text: edited,
      original_text,
    },
  ];
}
